import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, takeUntil, Subject, debounceTime, distinctUntilChanged, Subscription, filter } from 'rxjs';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { authNicknameErrorMap, templateNicknameErrorMap } from '../../../shared/utilities/Constants';
import { isEmptyString } from '../../../shared/utilities/FormValidators';
import { nicknameAllowedCharacters } from '../../../shared/utilities/regex-patterns';
import { ReviewPageActions } from '../../actions';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { AdobeHelpers } from '../../../shared/adobe-tagging/adobeHelper';
import { AdobeConstants } from '../../../shared/adobe-tagging/AdobeConstants';
import { DraftCommonWire, Drafts } from '../../models/wire/WireTemplatesResponse';
import { selectTemplateId } from '../../reducers';
import { selectTemplatesList } from '../../../account-info-header/reducers';

interface SaveAsTemplateOptionsForm {
  templateNickname?: FormControl<string | null>;
}

const templateNicknameControl = 'templateNickname';
@Component({
  standalone: false,
  selector: 'mm-templates-option',
  templateUrl: './templates-option.component.html',
  styleUrls: ['./templates-option.component.scss'],
})
export class TemplatesOptionComponent implements OnInit {
  @Input()
  shouldToggleOff$: Observable<boolean>;

  @Output()
  toggledOn: EventEmitter<boolean> = new EventEmitter();

  toggleOff: boolean = true;
  templates: DraftCommonWire[];
  selectedTemplateID: string;
  showNicknameControl: boolean = false;
  optionsForm = new FormGroup<SaveAsTemplateOptionsForm>({});
  templateMessaging: string =
    'We will save these transaction details for future use. Client authorization may be required on the transaction, as templates are not a standing authorization.';

  nicknameErrorMessage = authNicknameErrorMap.pattern;
  nicknameModel: TextInputModel = {
    controlName: templateNicknameControl,
    label: 'Nickname',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 40,
    allowedChars: nicknameAllowedCharacters,
  };

  nicknameSub: Subscription | undefined;

  destroy$: Subject<boolean> = new Subject<boolean>();
  constructor(private ffs: FeatureFlagService, private store: Store ) {}

  ngOnInit(): void {
    this.shouldToggleOff$
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldToggleOff) => {
        if (shouldToggleOff) {
          this.toggleOff = true;
          this.showNicknameControl = false;
          this.nicknameControl?.reset('');
        }
      });

    this.store.select(selectTemplatesList).pipe(takeUntil(this.destroy$)).subscribe(templates => this.templates = templates);
    this.store.select(selectTemplateId).pipe(takeUntil(this.destroy$)).subscribe(id => this.selectedTemplateID = id ?? '');
    const templateInContext = this.templates.find(template => template.draftId === this.selectedTemplateID);
    if (templateInContext && !templateInContext?.draftObject?.isIsoCompliant) {
        this.toggleOff = false;
        this.optionsForm.setControl(
          templateNicknameControl,
          new FormControl<string | null>('', [Validators.pattern(nicknameAllowedCharacters), isEmptyString])
        );
        this.showNicknameControl = true;

      this.nicknameSub = this.nicknameControl?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(nickname => this.nicknameControl?.valid ?? false)
      ).subscribe(nickname => {
        this.store.dispatch(ReviewPageActions.updateTemplateNickname({ nickname: nickname?.trim() ?? '' }))
      })

      this.store.dispatch(ReviewPageActions.saveAsTemplateToggled({isSaveAsTemplate: !this.toggleOff}));
    }
  }

  ngAfterViewInit(): void {
    AdobeHelpers.trackClick( 'save_template_toggle', AdobeConstants.ADOBE_ + AdobeConstants._SAVETEMPLATE, 'change');
  }

  ngOnDestroy(): void {
    this.nicknameSub?.unsubscribe();

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  /**
   *
   * @param toggleValue Boolean value from the toggle switch. true = checked/ON, false = unchecked/OFF
   *
   */
  onToggleChange(toggleValue: Event) {
    const e = toggleValue as CustomEvent<boolean>;
    if (e.detail) {
      this.toggleOff = false;
      this.optionsForm.setControl(
        templateNicknameControl,
        new FormControl<string | null>('', [Validators.pattern(nicknameAllowedCharacters), isEmptyString])
      );
      this.showNicknameControl = true;

      this.nicknameSub = this.nicknameControl?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(nickname => this.nicknameControl?.valid ?? false)
      ).subscribe(nickname => {
        this.store.dispatch(ReviewPageActions.updateTemplateNickname({ nickname: nickname?.trim() ?? '' }))
      })
    }
    else {
      this.showNicknameControl = false;
      this.optionsForm.removeControl(templateNicknameControl);
      this.nicknameSub?.unsubscribe();
    }
    this.toggledOn.emit(e.detail);
  }

  onChangeToggle(toggleValue: boolean) {
    if (toggleValue) {
      this.toggleOff = false;
      this.optionsForm.setControl(
        templateNicknameControl,
        new FormControl<string | null>('', [Validators.pattern(nicknameAllowedCharacters), isEmptyString])
      );
      this.showNicknameControl = true;

      this.nicknameSub = this.nicknameControl?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(nickname => this.nicknameControl?.valid ?? false)
      ).subscribe(nickname => {
        this.store.dispatch(ReviewPageActions.updateTemplateNickname({ nickname: nickname?.trim() ?? '' }))
      })
    }
    else {
      this.showNicknameControl = false;
      this.optionsForm.removeControl(templateNicknameControl);
      this.nicknameSub?.unsubscribe();
    }
    this.toggledOn.emit(toggleValue);
  }

  get nicknameControl(): FormControl<string | null> | undefined {
    return this.optionsForm.controls.templateNickname;
  }

  get nicknameError() {
    if (this.nicknameControl && this.nicknameControl.errors) {
      const errorKeys: string[] = Object.keys(this.nicknameControl.errors ?? {});
      if (errorKeys.length > 0) {
        return templateNicknameErrorMap[
          errorKeys[0] as keyof typeof templateNicknameErrorMap
        ];
      }
    }
  }
}
